/**
 * Shared display vocabulary for the FI pledge surfaces: Pledge Inbox, Active
 * Pledges, Pledge Detail and FI Tasks.
 *
 * The four pages each carried their own status badge, their own CHA tier
 * rendering (a colored badge on Active Pledges, plain text on the Inbox and
 * the detail page) and their own column order for the same PledgeSummary
 * fields. They now read one set of label maps, one badge per enum and one
 * column builder from here.
 *
 * Every map is keyed by a GENERATED enum type, so a member added to the spec
 * fails tsc here until it has a label. Some wire fields that carry these values
 * are typed as free strings in platform-admin.openapi.yaml
 * (PledgeSelectedAccountDetail.accountType / .assetClass,
 * PledgeConsentProof.consentMethod), so a value outside the enum can still
 * arrive. It renders as "Unrecognized" with the received code in the title
 * attribute, never as the raw code on screen and never as a guessed label.
 * The CHA tier fields are typed as the four-tier ladder since iris cw-06.
 */

import { useMemo } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText } from 'lucide-react';
import { Badge, Button, formatCalendarDate, formatUsd } from '@aaimplatform/ui';
import type { ColumnDef } from '@aaimplatform/ui';
import type { components as CollateralPolicyComponents } from '@aaimplatform/institutional-types/collateral-policy';
import type { components as AssetClassificationComponents } from '@aaimplatform/institutional-types/asset-classification';
import type { components as FinancialProviderComponents } from '@aaimplatform/institutional-types/financial-provider';
import type { components as PlatformAdminComponents } from '@aaimplatform/institutional-types/platform-admin';
import { CHA_TIER_CLASSES } from '../collateral-policy/chaTier';

type PledgeStatus = PlatformAdminComponents['schemas']['PledgeStatus'];
type PledgeSummary = PlatformAdminComponents['schemas']['PledgeSummary'];
/**
 * The four-tier CHA ladder, RED terminal (collateral-policy.openapi.yaml
 * CollateralHealthTier; operator decision 2026-07-27, iris cw-06). It types
 * PledgeSummary.currentChaTier, PledgeDetail.currentChaTier / previousChaTier
 * and CollateralDeficiency.tier on the wire.
 */
export type CollateralHealthTier = CollateralPolicyComponents['schemas']['CollateralHealthTier'];
type AssetClass = AssetClassificationComponents['schemas']['AssetClass'];
type AccountType = FinancialProviderComponents['schemas']['AccountType'];
type AccountSubtype = FinancialProviderComponents['schemas']['AccountSubtype'];
type ConsentMethod = FinancialProviderComponents['schemas']['ConnectorAuthorizationConsent']['consentMethod'];

/** What a label lookup prints for a value outside its enum. */
export const UNRECOGNIZED_LABEL = 'Unrecognized';

// ---------------------------------------------------------------------------
// Label maps
// ---------------------------------------------------------------------------

export const PLEDGE_STATUS_LABEL: Record<PledgeStatus, string> = {
  PENDING: 'Pending',
  ACKNOWLEDGED: 'Acknowledged',
  ACTIVE: 'Active',
  RELEASED: 'Released',
  EXPIRED: 'Expired',
  WITHDRAWN: 'Withdrawn',
  DECLINED: 'Declined',
  RETURNED: 'Returned',
  // fi-person-noun-and-signature-label (operator ruling): the label names
  // whose signature is outstanding, in the FI noun, and reads the same in
  // platform-admin (utils/status-labels.tsx). Registered as term
  // pledge-status-awaiting-signature in terminology/terms/collateral.yaml.
  AWAITING_SIGNATURE: 'Awaiting customer signature',
};

export const CHA_TIER_ORDER: readonly CollateralHealthTier[] = ['GREEN', 'YELLOW', 'ORANGE', 'RED'];

export const CHA_TIER_LABEL: Record<CollateralHealthTier, string> = {
  GREEN: 'Green',
  YELLOW: 'Yellow',
  ORANGE: 'Orange',
  RED: 'Red',
};

/** A pledge whose projection carries no tier has not been assessed. */
export const CHA_TIER_UNCLASSIFIED_LABEL = 'Unclassified';

export const ASSET_CLASS_LABEL: Record<AssetClass, string> = {
  STABLECOINS: 'Stablecoins',
  PUBLIC_SECURITIES: 'Public securities',
  COMMERCIAL_REAL_ESTATE: 'Commercial real estate',
  RESIDENTIAL_REAL_ESTATE: 'Residential real estate',
  INCOME_PROPERTY: 'Income property',
  MAJOR_CRYPTOCURRENCY: 'Major cryptocurrency',
  PUBLIC_REITS: 'Public REITs',
  TOKENIZED_SECURITIES: 'Tokenized securities',
  PRIVATE_CREDIT: 'Private credit',
  NON_TRADED_REITS: 'Non-traded REITs',
  LP_INTERESTS_HEDGE_FUND: 'LP interests, hedge fund',
  LP_INTERESTS_PRIVATE_EQUITY: 'LP interests, private equity',
  RSU_RESTRICTED_STOCK: 'RSUs and restricted stock',
  PRIVATE_COMPANY_EQUITY: 'Private company equity',
  VENTURE_STARTUP_EQUITY: 'Venture and startup equity',
  US_TREASURIES: 'US Treasuries',
  CASH: 'Cash',
  OTHER_DIGITAL_ASSETS: 'Other digital assets',
  OPTIONS: 'Options',
  OTHER: 'Other',
};

/**
 * AccountType and AccountSubtype from financial-provider.openapi.yaml. The
 * pledge projection's accountType is a free string that carries either
 * vocabulary (the OICU seed pledges a 'brokerage' account, a member of both).
 */
export const ACCOUNT_TYPE_LABEL: Record<AccountType | AccountSubtype, string> = {
  depository: 'Depository',
  credit: 'Credit',
  loan: 'Loan',
  investment: 'Investment',
  brokerage: 'Brokerage',
  other: 'Other',
  checking: 'Checking',
  savings: 'Savings',
  money_market: 'Money market',
  cd: 'Certificate of deposit',
  cash_management: 'Cash management',
  paypal: 'PayPal',
  prepaid: 'Prepaid',
  credit_card: 'Credit card',
  line_of_credit: 'Line of credit',
  auto: 'Auto loan',
  mortgage: 'Mortgage',
  student: 'Student loan',
  personal: 'Personal loan',
  home_equity: 'Home equity',
  retirement: 'Retirement',
  '401k': '401(k)',
  '403b': '403(b)',
  ira: 'IRA',
  roth_ira: 'Roth IRA',
  hsa: 'HSA',
  mutual_fund: 'Mutual fund',
  trust: 'Trust',
};

export const CONSENT_METHOD_LABEL: Record<ConsentMethod, string> = {
  TYPED_NAME: 'Typed name',
  CHECKBOX_ACKNOWLEDGE: 'Checkbox acknowledgment',
};

function hasKey<K extends string>(map: Record<K, string>, value: string): value is K {
  return Object.prototype.hasOwnProperty.call(map, value);
}

export function isChaTier(value: string | null | undefined): value is CollateralHealthTier {
  return typeof value === 'string' && hasKey(CHA_TIER_LABEL, value);
}

/**
 * Label for a value drawn from a generated enum. `null`/`undefined` is an
 * absent field and prints `--`; a value outside the enum prints
 * {@link UNRECOGNIZED_LABEL}.
 */
export function enumLabel<K extends string>(
  map: Record<K, string>,
  value: string | null | undefined,
): string {
  if (value === null || value === undefined || value === '') return '--';
  return hasKey(map, value) ? map[value] : UNRECOGNIZED_LABEL;
}

/**
 * {@link enumLabel} as an element. An unrecognized value keeps the received
 * code in the title attribute so support can still read it.
 */
export function EnumText<K extends string>({
  map,
  value,
  className,
}: {
  map: Record<K, string>;
  value: string | null | undefined;
  className?: string;
}) {
  const label = enumLabel(map, value);
  const unrecognized = label === UNRECOGNIZED_LABEL;
  return (
    <span className={className} title={unrecognized && value ? value : undefined}>
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Badges
// ---------------------------------------------------------------------------

/**
 * The neutral badge surface. The preset defines no `info` color and no bare
 * `neutral` color, so `bg-info/10 text-info` and `bg-neutral/10` generated no
 * CSS and ACKNOWLEDGED rendered as unstyled text (op-g1-8). Every class here is
 * a preset token.
 */
const NEUTRAL_BADGE = 'bg-recessed text-secondary';

export function pledgeStatusClasses(status: string): string {
  switch (status) {
    case 'PENDING':
    case 'RETURNED':
    case 'AWAITING_SIGNATURE':
      return 'bg-warning/10 text-warning';
    case 'ACKNOWLEDGED':
      // In progress toward acceptance: the neutral surface, primary text.
      return 'bg-recessed text-primary';
    case 'ACTIVE':
      return 'bg-growth/10 text-growth';
    case 'DECLINED':
    case 'WITHDRAWN':
      return 'bg-error/10 text-error';
    default:
      return NEUTRAL_BADGE;
  }
}

export function PledgeStatusBadge({ status }: { status: string }) {
  const label = enumLabel(PLEDGE_STATUS_LABEL, status);
  return (
    <span
      className={`inline-flex px-3 py-1 text-xs font-sans font-semibold rounded-sm leading-normal ${pledgeStatusClasses(status)}`}
      title={label === UNRECOGNIZED_LABEL ? status : undefined}
    >
      {label}
    </span>
  );
}

/**
 * The status column cell. An AWAITING_SIGNATURE pledge carries a second line
 * saying whether the FI already acknowledged it (GH #2598): acknowledgment does
 * not move that status, so without the line an acknowledged pledge and an
 * unacknowledged one read the same. The badge already names the wait, so the
 * line names the acknowledgment alone.
 */
export function PledgeStatusCell({ pledge }: { pledge: Pick<PledgeSummary, 'status' | 'acknowledgedBy'> }) {
  if (pledge.status !== 'AWAITING_SIGNATURE') return <PledgeStatusBadge status={pledge.status} />;
  return (
    <div>
      <PledgeStatusBadge status={pledge.status} />
      <div className="text-xs font-sans text-tertiary leading-normal mt-1">
        {pledge.acknowledgedBy ? 'Acknowledged' : 'Not yet acknowledged'}
      </div>
    </div>
  );
}

/**
 * Badge classes for a tier, read from the one CHA tier map the policy editors
 * use (pages/collateral-policy/chaTier.ts). This file carried a third copy that
 * gave ORANGE the same `warning` text as YELLOW, so the two tiers read as one
 * color on every pledge list (fi-g7-6); ORANGE now takes `warning-bright`. A
 * missing or unrecognized tier takes the neutral surface.
 */
export function chaTierClasses(tier: string | null | undefined): string {
  if (!isChaTier(tier)) return NEUTRAL_BADGE;
  const classes = CHA_TIER_CLASSES[tier];
  return `${classes.panel} ${classes.text}`;
}

/**
 * The CHA tier as the one badge every pledge surface uses. No tier prints
 * "Unclassified". The pledge and deficiency tier fields are typed as the
 * four-tier ladder (iris cw-06) and platform-admin-api omits a stored value off
 * it, so they arrive as a tier or not at all. A caller that passes another
 * string still gets "Unrecognized" with the code in its title.
 */
export function ChaTierBadge({ tier }: { tier: string | null | undefined }) {
  const label = tier ? enumLabel(CHA_TIER_LABEL, tier) : CHA_TIER_UNCLASSIFIED_LABEL;
  return (
    <span
      className={`inline-flex px-2.5 py-1 text-xs font-sans font-semibold rounded-sm leading-normal ${chaTierClasses(tier)}`}
      title={label === UNRECOGNIZED_LABEL && tier ? tier : undefined}
    >
      {label}
    </span>
  );
}

/**
 * Eligibility of a pledged account or holding, the same words and the same kit
 * badge wherever it prints. The detail page printed "Eligible"/"Ineligible" for
 * an account and "Yes"/"No" for a holding, each in its own hand-rolled span
 * (fi-g1-12).
 */
export function EligibilityBadge({ eligible }: { eligible: boolean }) {
  return (
    <Badge variant={eligible ? 'success' : 'error'} size="small">
      {eligible ? 'Eligible' : 'Ineligible'}
    </Badge>
  );
}

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

/**
 * A pledge amount to the cent, the kit's table-cell form ('$81,250.00'), so a
 * pledge amount prints the same way on every admin page (r3 fi-cons-1).
 */
export function formatCurrencyFull(value: number | null | undefined): string {
  if (value === null || value === undefined) return '--';
  return formatUsd(value);
}

/**
 * A pledge list's Created date: the calendar date of the instant the detail
 * page prints in full with its time and zone, so one pledge's createdAt reads
 * as the same day on every page (fi-g1-6).
 */
export function formatDateShort(value: string | null | undefined): string {
  if (!value) return '--';
  return formatCalendarDate(value);
}

/** "1 pledge", "20 pledges": a count with its noun agreeing (fi-g1-8). */
export function pledgeCount(n: number): string {
  return `${n.toLocaleString('en-US')} ${n === 1 ? 'pledge' : 'pledges'}`;
}

// ---------------------------------------------------------------------------
// Pledge list columns (Inbox + Active Pledges)
// ---------------------------------------------------------------------------

/** Where the detail page's back link returns to. */
export interface PledgeBackTargetState {
  pathname: string;
  label: string;
}

export const BACK_TO_INBOX: PledgeBackTargetState = { pathname: '/pledges', label: 'Back to Pledge Inbox' };
export const BACK_TO_ACTIVE: PledgeBackTargetState = { pathname: '/pledges/active', label: 'Back to Active Pledges' };

/** The row action label, the same on every pledge list. */
export const PLEDGE_ROW_ACTION_LABEL = 'View';

/**
 * The pledge-list search placeholder. It fits the phone search field (about 43
 * characters), where the 55-character form was clipped mid-word, and it doubles
 * as the input's accessible name, so it names what the search reads (fi-g1-17).
 * Institution is not among them: every pledge on these lists is the reader's
 * own institution (GH #678), so the lists neither show nor search `fiName`.
 */
export const PLEDGE_SEARCH_PLACEHOLDER = 'Package ID, customer, email';

/**
 * One column set, one order, one action label for every PledgeSummary list.
 * `from` is the back target the detail page's link returns to.
 */
export function usePledgeSummaryColumns(from: PledgeBackTargetState): ColumnDef<PledgeSummary, unknown>[] {
  const navigate = useNavigate();
  return useMemo<ColumnDef<PledgeSummary, unknown>[]>(() => {
    const open = (id: string) => {
      void navigate(`/pledges/${id}`, { state: { from } });
    };
    const money = (value: unknown): ReactNode => (
      <span className="text-sm font-mono text-primary tabular-nums leading-normal">
        {formatCurrencyFull(value as number | null | undefined)}
      </span>
    );
    return [
      {
        // The one navigation control per row (fi-g1-14). The package ID, the
        // member name and the View button each opened the same page, so a row
        // cost three tab stops and gave a screen reader three unnamed "open"
        // actions. The package ID carries the accessible name; the member name
        // is text; View stays visible for the pointer but out of the tab order
        // and the accessibility tree.
        accessorKey: 'collateralPackageId',
        header: 'Package ID',
        cell: ({ row }) => (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              open(row.original.id);
            }}
            className="text-sm font-mono text-accent hover:text-accent/80 hover:underline tabular-nums leading-normal text-left"
            aria-label={`Open pledge ${row.original.collateralPackageId}`}
          >
            {row.original.collateralPackageId}
          </button>
        ),
        size: 200,
      },
      {
        accessorKey: 'memberEmail',
        // fi-customer: a pledge page names the person "Customer" (ruling
        // 2026-09-29); loan pages keep "Borrower".
        header: 'Customer',
        cell: ({ row }) => (
          <div>
            <div className="text-sm font-sans font-medium text-primary leading-normal">
              {row.original.memberName ?? row.original.memberEmail}
            </div>
            {row.original.memberName && (
              <div className="text-xs font-sans text-tertiary leading-normal">
                {row.original.memberEmail}
              </div>
            )}
            {/* No institution line (GH #678, operator ruling 2026-10-23): every
                row on an admin-portal pledge list is the reader's own
                institution, so `fiName` repeated one name on every row. */}
          </div>
        ),
        size: 240,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => <PledgeStatusCell pledge={row.original} />,
        size: 130,
      },
      {
        accessorKey: 'currentChaTier',
        header: 'CHA Tier',
        cell: ({ getValue }) => <ChaTierBadge tier={getValue() as string | undefined} />,
        size: 130,
      },
      {
        accessorKey: 'totalCollateralValue',
        header: 'Collateral Value',
        cell: ({ getValue }) => money(getValue()),
        size: 160,
      },
      {
        accessorKey: 'totalPledgeableAmount',
        header: 'Pledgeable Amount',
        cell: ({ getValue }) => money(getValue()),
        size: 170,
      },
      {
        accessorKey: 'accountCount',
        header: 'Accounts',
        cell: ({ getValue }) => (
          <span className="text-sm font-mono text-primary tabular-nums leading-normal">
            {getValue() as number}
          </span>
        ),
        size: 90,
      },
      {
        accessorKey: 'createdAt',
        header: 'Created',
        cell: ({ getValue }) => (
          <span className="text-sm font-sans text-secondary leading-normal">
            {formatDateShort(getValue() as string)}
          </span>
        ),
        size: 120,
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <Button
            variant="secondary"
            size="sm"
            icon={FileText}
            onClick={() => open(row.original.id)}
            tabIndex={-1}
            aria-hidden="true"
          >
            {PLEDGE_ROW_ACTION_LABEL}
          </Button>
        ),
        size: 100,
      },
    ];
  }, [navigate, from]);
}

/**
 * Coverage of a list read against the server's own total. The pledge list
 * endpoint pages (platform-admin.openapi.yaml /pledges, `limit` default 20),
 * so a figure summed over what one page carried is a page subtotal whenever
 * the server holds more. `partial` tells a tile not to present it as the book.
 */
export function listCoverage(read: number, total: number | null | undefined): {
  read: number;
  total: number | null;
  partial: boolean;
} {
  const resolved = typeof total === 'number' ? total : null;
  return { read, total: resolved, partial: resolved !== null && resolved > read };
}
